import { useNavigate, useParams } from 'react-router';
import ActionHeader from '../../../components/actionHeader';
import {
  Button,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  message,
  Row,
  Select,
  Spin,
  Typography,
} from 'antd';
import Page from '../../../components/page';
import { Container, SectionTitle } from './styles';
import ImageUpload from '../../../components/imageUpload';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  createMachine,
  getMachineById,
  updateMachine,
} from '../../../utils/httpRequest';
import { File, Machine, UploadBusinessType } from '../../../types';
import { useAppDispatch } from '../../../store';
import { fetchTemplates, selectTemplates } from '../../../slices/templates';
import { useSelector } from 'react-redux';

const MachineForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [machine, setMachine] = useState<Machine>();
  const [images, setImages] = useState<File[]>([]);
  const imagesRef = useRef<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  const templates = useSelector(selectTemplates);

  const handleImageUploaded = (response: any) => {
    const raw = response?.data?.[0];
    const file = raw
      ? { id: raw.id, url: raw.fileUrl || raw.url, filename: raw.filename }
      : undefined;
    const newImages = file ? [file] : [];
    setImages(newImages);
    imagesRef.current = newImages;
    console.log({ newImages });
  };

  const handleConfirm = () => {
    if (isUploading) {
      return;
    }
    setLoading(true);
    form
      .validateFields()
      .then(async () => {
        const newMachine = {
          machineID: id || 0,
          images: imagesRef.current,
          ...form.getFieldsValue(),
          description: '1231',
          documents: machine?.documents || [],
        };
        if (id) {
          await updateMachine(newMachine);
        } else {
          await createMachine(newMachine);
        }
        messageApi.success(id ? t('machine_updated') : t('machine_created'));
        navigate(-1);
      })
      .catch((error) => {
        console.log(error);
        messageApi.error(t('submit_failed'));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getMachineDetails = async (machineId: string) => {
    try {
      setLoading(true);
      const { data } = await getMachineById(machineId);

      const machineDetails = {
        ...data,
        constructionDate: data.constructionDate
          ? dayjs(data.constructionDate)
          : null,
        acceptanceDate: data.acceptanceDate ? dayjs(data.acceptanceDate) : null,
      };

      form.setFieldsValue(machineDetails);
      setMachine(machineDetails);
      const newImages = machineDetails?.images || [];
      setImages(newImages);
      imagesRef.current = newImages;
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      getMachineDetails(id);
    }
  }, [id]);

  useEffect(() => {
    dispatch(fetchTemplates());
  }, []);

  return (
    <Page
      backgroundColor="#fff"
      header={
        <ActionHeader
          title={machine?.name || t('machine_add_new')}
          showBack
          onBack={() => navigate(-1)}
          endSlot={
            <Flex vertical gap={4} align="flex-end">
              <Button
                type="primary"
                onClick={handleConfirm}
                disabled={isUploading}
              >
                {t('confirm')}
              </Button>
              {isUploading && (
                <Typography.Text style={{ fontSize: 12 }}>
                  {t('images_uploading')}
                </Typography.Text>
              )}
            </Flex>
          }
        />
      }
    >
      {contextHolder}
      <Spin spinning={loading} fullscreen></Spin>
      <Form form={form} layout="vertical">
        <Container vertical gap={16}>
          <Flex vertical gap={13}>
            <SectionTitle>{t('basic_information')}</SectionTitle>
            <Row gutter={[40, 16]}>
              <Col span={8}>
                <ImageUpload
                  imageUrl={images?.[0]?.url}
                  businessType={UploadBusinessType.MACHINE}
                  onUploaded={handleImageUploaded}
                  onLoadingChange={setIsUploading}
                />
              </Col>
              <Col span={16}>
                <Row gutter={[40, 0]}>
                  <Col span={12}>
                    <Form.Item
                      label={t('machine_name')}
                      name="name"
                      rules={[
                        {
                          required: true,
                          message: t('required_machine_name'),
                        },
                      ]}
                    >
                      <Input type="text" placeholder={t('enter')} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label={t('alias')} name="alias">
                      <Input type="text" placeholder={t('enter')} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label={t('location')} name="location">
                      <Input type="text" placeholder={t('enter')} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label={t('unique_id')} name="serviceNumber">
                      <Input type="text" placeholder={t('enter')} />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Flex>
          <SectionTitle>{t('additional_information')}</SectionTitle>
          <Row gutter={[40, 0]}>
            <Col span={8}>
              <Form.Item label={t('template')} name="templateID">
                <Select
                  placeholder={t('select')}
                  options={templates.map((i) => ({
                    value: i.templateID,
                    label: i.name,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label={t('machine_type')} name="type">
                <Input type="text" placeholder={t('enter')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label={t('manufacturer')} name="manufacturer">
                <Input type="text" placeholder={t('enter')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label={t('serial_number')} name="serialNumber">
                <Input type="text" placeholder={t('enter')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label={t('construction_date')} name="constructionDate">
                <DatePicker
                  placeholder={t('select_date')}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label={t('acceptance_date')} name="acceptanceDate">
                <DatePicker
                  placeholder={t('select_date')}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Container>
      </Form>
    </Page>
  );
};

export default MachineForm;
